import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { Brand, BrandId, brands } from '../theme/brands';
interface BrandContextValue {
  brand: Brand;
  /** Swaps the operator name into shared copy so one content set serves every client brand. */
  text: (value: string) => string;
  programImage: (slug: string, fallback: string) => string;
  productImage: (slug: string, fallback: string) => string;
}
const BrandContext = createContext<BrandContextValue | null>(null);
export function BrandProvider({
  brandId,
  children
}: {
  brandId: BrandId;
  children: React.ReactNode;
}) {
  const brand = brands[brandId] ?? brands.republic;
  useEffect(() => {
    document.documentElement.setAttribute('data-brand', brand.id);
  }, [brand.id]);

  // The tab icon is drawn from the brand mark itself, so a new brand needs no asset upload.
  useEffect(() => {
    const href = `data:image/svg+xml,${encodeURIComponent(brand.favicon)}`;
    const apply = (rel: string) => {
      let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('type', 'image/svg+xml');
      link.setAttribute('href', href);
    };
    apply('icon');
    apply('apple-touch-icon');
  }, [brand.favicon]);
  const text = useCallback((value: string) => brand.id === 'republic' ? value : value.replace(/Republic/g, brand.name), [brand.id, brand.name]);
  const programImage = useCallback((slug: string, fallback: string) => brand.images.programs[slug] ?? fallback, [brand]);
  const productImage = useCallback((slug: string, fallback: string) => brand.images.products[slug] ?? fallback, [brand]);
  const value = useMemo(() => ({
    brand,
    text,
    programImage,
    productImage
  }), [brand, text, programImage, productImage]);
  return <BrandContext.Provider value={value} data-id="element-279" data-mp="6smsehnn5d1e">{children}</BrandContext.Provider>;
}
export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used inside a BrandProvider');
  }
  return context;
}